import React from 'react';
import { Props } from '.';
import Icon from '../../../components/icon/Icon';
import { ClockState } from '../../../core/models/enum';
import { splitWholeSeconds } from '../../../helpers/countdownClock';
import { EventPeriodEnum } from '../../../utils/api';

class MatchClock extends React.PureComponent<Props> {
    // Saving is non-blocking: the check just hands the event to the background
    // queue, which reports loading/success/error in a corner notification.
    private onConfirmation = (): void => {
        this.props.saveEvent();
    };

    public render = (): React.ReactElement => {
        const {
            event,
            clockState,
            eventTime,
            eventPeriod,
            overtimeUiMode,
            isEventValid,
            startClock,
            stopClock,
            setPeriod,
            modifyClock,
        } = this.props;

        // Fraction stays in state for accurate start/stop; UI shows whole seconds.
        const { minute: clockMinutes, seconds: clockSeconds } = splitWholeSeconds(eventTime);
        const clockMinutesDisplay = clockMinutes < 10 ? `0${clockMinutes}` : clockMinutes;
        const clockSecondsDisplay = clockSeconds < 10 ? `0${clockSeconds}` : clockSeconds;

        let className = '';

        switch (clockState) {
            case ClockState.Running:
                className = 'is-running';
                break;
            case ClockState.Paused:
            case ClockState.Stopped:
                className = isEventValid ? 'is-time-confirmation-active' : 'is-paused';
                break;
            default:
                break;
        }
        const stopwatchClickHandler =
            clockState === ClockState.Running ? (): void => stopClock() : (): void => startClock();
        const isEditingEvent = event != null;

        return (
            <div className="o-clock-screen noselect">
                <div className={`c-clock ${className}`}>
                    <div className="c-clock__period-panel">
                        <div
                            className="c-clock__period-pills-wrap"
                            role="group"
                            aria-label="Period for the event being recorded"
                        >
                            <div className="c-clock__period-pills c-clock__period-pills--regular">
                                <span
                                    onClick={(): void => setPeriod(EventPeriodEnum.First_Period)}
                                    className={
                                        eventPeriod === EventPeriodEnum.First_Period
                                            ? 'is-active'
                                            : ''
                                    }
                                    role="button"
                                    title="1st Period"
                                >
                                    Q1
                                </span>
                                <span
                                    onClick={(): void => setPeriod(EventPeriodEnum.Second_Period)}
                                    className={
                                        eventPeriod === EventPeriodEnum.Second_Period
                                            ? 'is-active'
                                            : ''
                                    }
                                    role="button"
                                    title="2nd Period"
                                >
                                    Q2
                                </span>
                                <span
                                    onClick={(): void => setPeriod(EventPeriodEnum.Third_Period)}
                                    className={
                                        eventPeriod === EventPeriodEnum.Third_Period
                                            ? 'is-active'
                                            : ''
                                    }
                                    role="button"
                                    title="3rd Period"
                                >
                                    Q3
                                </span>
                                <span
                                    onClick={(): void => setPeriod(EventPeriodEnum.Fourth_Period)}
                                    className={
                                        eventPeriod === EventPeriodEnum.Fourth_Period
                                            ? 'is-active'
                                            : ''
                                    }
                                    role="button"
                                    title="4th Period"
                                >
                                    Q4
                                </span>
                            </div>
                            <div className="c-clock__period-divider" aria-hidden="true" />
                            <div className="c-clock__period-ot">
                                <span
                                    onClick={(): void =>
                                        setPeriod(EventPeriodEnum.Overtime, 'play')
                                    }
                                    className={
                                        eventPeriod === EventPeriodEnum.Overtime &&
                                        overtimeUiMode === 'play'
                                            ? 'is-active'
                                            : ''
                                    }
                                    role="button"
                                    title="Overtime"
                                >
                                    OT
                                </span>
                                <span
                                    onClick={(): void =>
                                        setPeriod(EventPeriodEnum.Overtime, 'penalty_shootout')
                                    }
                                    className={
                                        eventPeriod === EventPeriodEnum.Overtime &&
                                        overtimeUiMode === 'penalty_shootout'
                                            ? 'is-active'
                                            : ''
                                    }
                                    role="button"
                                    title="Penalty shootout (same period as OT)"
                                >
                                    PSO
                                </span>
                            </div>
                        </div>
                    </div>
                    <div
                        className={`c-clock__clock ${
                            clockState !== ClockState.Running ? 'enabled' : ''
                        }`}
                    >
                        <div
                            className="c-clock__pause"
                            onClick={isEditingEvent ? undefined : stopwatchClickHandler}
                            role="button"
                        >
                            <div className="c-clock__pause-inner">
                                <Icon name="icon-stopwatch" />
                            </div>
                        </div>
                        <div className="c-clock__minutes">
                            <div className="c-clock__minus-plus">
                                <span
                                    onClick={
                                        clockState !== ClockState.Running
                                            ? (): void => modifyClock(60)
                                            : undefined
                                    }
                                    className="c-clock__plus"
                                    role="button"
                                >
                                    <Icon name="icon-plus" />
                                </span>
                                <span
                                    onClick={
                                        clockState !== ClockState.Running
                                            ? (): void => modifyClock(-60)
                                            : undefined
                                    }
                                    className="c-clock__minus"
                                    role="button"
                                >
                                    <Icon name="icon-minus" />
                                </span>
                            </div>
                            <div className="c-clock__digits">{clockMinutesDisplay}</div>
                        </div>
                        <div className="c-clock__colon">:</div>
                        <div className="c-clock__seconds">
                            <div className="c-clock__minus-plus">
                                <span
                                    onClick={
                                        clockState !== ClockState.Running
                                            ? (): void => modifyClock(10)
                                            : undefined
                                    }
                                    className="c-clock__plus"
                                    role="button"
                                >
                                    <Icon name="icon-plus" />
                                </span>
                                <span
                                    onClick={
                                        clockState !== ClockState.Running
                                            ? (): void => modifyClock(-10)
                                            : undefined
                                    }
                                    className="c-clock__minus"
                                    role="button"
                                >
                                    <Icon name="icon-minus" />
                                </span>
                            </div>
                            <div className="c-clock__digits">{clockSecondsDisplay}</div>
                            <div className="c-clock__minus-plus">
                                <span
                                    onClick={
                                        clockState !== ClockState.Running
                                            ? (): void => modifyClock(1)
                                            : undefined
                                    }
                                    className="c-clock__plus"
                                    role="button"
                                >
                                    <Icon name="icon-plus" />
                                </span>
                                <span
                                    onClick={
                                        clockState !== ClockState.Running
                                            ? (): void => modifyClock(-1)
                                            : undefined
                                    }
                                    className="c-clock__minus"
                                    role="button"
                                >
                                    <Icon name="icon-minus" />
                                </span>
                            </div>
                        </div>
                        <div
                            className="c-clock__confirmation"
                            onClick={isEventValid ? (): void => this.onConfirmation() : undefined}
                            role="button"
                        >
                            <Icon name="icon-check" />
                        </div>
                    </div>
                </div>
            </div>
        );
    };
}

export default MatchClock;
